import { useNavigate } from "react-router-dom";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { PlayerExternalLinks } from "../../leaderboard/PlayerExternalLinks";
import { PlayerRoleBadge } from "../../leaderboard/PlayerRoleBadge";
import { championIconUrl } from "../../../utils/championStats";
import { getMultikillLabel, getMultikillTier } from "../../../utils/multikill";
import { normalizeRole } from "../../../utils/leaderboard/roles";
import { matchDetailUrl } from "../../../utils/matchDetail";
import {
  assistsPerGameTier,
  computeKda,
  csPerMinTier,
  deathsPerGameTier,
  dpmTier,
  kdaTier,
  killsPerGameTier,
  kpPctTier,
} from "../../../utils/playerStatTiers";
import { StatValue } from "../../shared/StatValue";
import { StatTierLegend } from "../../shared/StatTierLegend";
import { TeamLogo } from "../../shared/TeamLogo";
import { PageMeta } from "../../../seo/PageMeta";
import { championIconById, ROLE_ICONS, type PlayerPosition } from "../constants";
import { usePlayerPage } from "../hooks/usePlayerPage";

const formatNum = (value: number | undefined, digits = 1): string =>
  value == null ? "—" : value.toFixed(digits);

const multikillTierClass: Record<string, string> = {
  double: "text-[#d8d8ef] bg-[rgba(122,122,154,0.2)]",
  triple: "text-[#6ee7a0] bg-[rgba(110,231,160,0.12)]",
  quadra: "text-[#5cc2ff] bg-[rgba(92,194,255,0.12)]",
  penta: "text-[#ffb347] bg-[rgba(255,179,71,0.15)]",
};

const sectionHeadingClass = "fh-title-section text-left";

export const PlayerPage = () => {
  const navigate = useNavigate();
  const { localized } = useLocalePath();
  const {
    copy,
    strings,
    pathname,
    internalPath,
    rosterPlayer,
    teamName,
    seasonStats,
    loading,
    error,
    champStatsExpanded,
    setChampStatsExpanded,
    displayName,
    hasRank,
    displayRank,
    rankIconUrl,
    championStats,
    sortedGames,
    totalGamesPlayed,
    uniqueChampionsPlayed,
    hiddenChampionCount,
    showChampToggle,
    showMultikillColumn,
    hasContent,
    showNotFound,
    playerDescription,
  } = usePlayerPage();

  return (
    <div className="mx-auto mt-40 min-h-screen w-[min(96%,1600px)] px-0 pb-16 pt-5 max-md:mt-32 max-md:w-[94%]">
      <PageMeta
        title={displayName}
        description={playerDescription}
        pathname={pathname}
        internalPath={internalPath}
      />
      {showNotFound ? (
        <p className="px-0 py-8 text-center text-[#aaa]">Pelaajaa ei löytynyt.</p>
      ) : null}

      {hasContent ? (
        <div className="grid grid-cols-[minmax(280px,340px)_minmax(0,1fr)] items-start gap-6 max-[960px]:grid-cols-1">
          <aside className="sticky top-28 flex flex-col gap-5 max-[960px]:static [&_.player-external-link]:inline-flex [&_.player-external-link]:h-8 [&_.player-external-link]:w-8 [&_.player-external-link]:items-center [&_.player-external-link]:justify-center [&_.player-external-link]:rounded-lg [&_.player-external-link]:border [&_.player-external-link]:border-[var(--surface-card-border)] [&_.player-external-link]:bg-[rgba(45,45,68,0.5)] [&_.player-external-link]:transition-[background,border-color,transform] [&_.player-external-link]:duration-150 [&_.player-external-link_img]:block [&_.player-external-link_img]:rounded-[2px] [&_.player-external-link:hover]:-translate-y-px [&_.player-external-link:hover]:border-[rgba(123,147,255,0.35)] [&_.player-external-link:hover]:bg-[rgba(123,147,255,0.12)] [&_.player-external-links]:mt-[0.35rem] [&_.player-external-links]:flex [&_.player-external-links]:w-full [&_.player-external-links]:flex-wrap [&_.player-external-links]:justify-center [&_.player-external-links]:gap-[0.45rem] [&_.player-external-links]:pr-3">
            <section className="surface-card flex flex-col items-center gap-4 p-6 text-center">
              <div className="flex w-full flex-col items-center gap-2">
                <h1 className="fh-title-entity w-full max-w-full break-words text-center">
                  {displayName}
                </h1>
                {rosterPlayer ? (
                  <img
                    src={ROLE_ICONS[rosterPlayer.position as PlayerPosition]}
                    alt={rosterPlayer.position}
                    width={32}
                    height={32}
                    className="mt-[0.15rem] h-8 w-8 brightness-[1.2] drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                  />
                ) : null}
                {rosterPlayer?.teamId ? (
                  <TeamLogo
                    teamId={rosterPlayer.teamId}
                    alt={teamName ?? rosterPlayer.teamId}
                    className="mt-[0.15rem] h-10 w-10 object-contain"
                  />
                ) : null}
              </div>

              {rosterPlayer ? (
                <div className="box-border flex w-full items-center justify-center gap-3 rounded-[10px] border border-[var(--surface-card-border)] bg-[rgba(45,45,68,0.5)] px-[1.1rem] py-[0.85rem]">
                  <img
                    src={rankIconUrl}
                    alt={displayRank}
                    width={44}
                    height={44}
                    className="h-11 w-11"
                  />
                  <div>
                    <p className="m-0 font-shentox text-[0.85rem] font-bold uppercase text-[#eee]">
                      {displayRank}
                    </p>
                    {hasRank ? (
                      <p className="mt-[0.15rem] text-left font-shentox text-[0.85rem] font-semibold text-[#ffcc33]">
                        {rosterPlayer.leaguePoint} LP
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {rosterPlayer ? (
                <PlayerExternalLinks
                  displayName={rosterPlayer.displayName || rosterPlayer.name}
                  tagLine={rosterPlayer.tagline}
                  gameName={rosterPlayer.name}
                  rankSiteLabel={(site) =>
                    strings.LEADERBOARD.VIEW_PLAYER_RANK_SITE.replace("{site}", site)
                  }
                />
              ) : null}

              {rosterPlayer && rosterPlayer.championMastery.length > 0 ? (
                <div className="flex w-full flex-wrap justify-center gap-[0.65rem] border-t border-[var(--surface-card-border)] pt-4">
                  {rosterPlayer.championMastery.map((champ) => (
                    <div key={champ.championId} className="flex flex-col items-center gap-1">
                      <img
                        src={championIconById(champ.championId)}
                        alt=""
                        width={34}
                        height={34}
                        className="h-[34px] w-[34px] rounded-full border-2 border-[#3a3a5a] object-cover"
                      />
                      <span className="text-[0.6rem] text-[#7a7a9a]">Lv{champ.championLevel}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>

            {!loading && championStats.length > 0 ? (
              <section
                className={[
                  "rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] px-5 py-[1.15rem]",
                  showChampToggle && !champStatsExpanded
                    ? "max-[960px]:[&_article:nth-child(n+4)]:hidden"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="mb-[0.85rem] flex items-baseline justify-between gap-3">
                  <h2 className={sectionHeadingClass}>{copy.CHAMPIONS}</h2>
                  <span className="fh-caption whitespace-nowrap">
                    {copy.UNIQUE_CHAMPS.replace("{count}", String(uniqueChampionsPlayed))}
                  </span>
                </div>
                <div className="flex max-h-[420px] flex-col gap-[0.65rem] overflow-y-auto max-[960px]:max-h-none">
                  {championStats.map((row) => (
                    <article
                      key={row.champion}
                      className="surface-card-row flex items-center gap-3 rounded-[var(--surface-card-radius-sm)] border border-[var(--surface-card-border)] px-3 py-[0.65rem]"
                    >
                      <img
                        src={championIconUrl(row.champion)}
                        alt={row.champion}
                        width={38}
                        height={38}
                        loading="lazy"
                        className="h-[38px] w-[38px] shrink-0 rounded-full border-2 border-[#3a3a5a] bg-[#12121f] object-cover"
                      />
                      <div className="min-w-0 flex-1 text-left">
                        <p className="fh-title-card">{row.champion}</p>
                        <p className="fh-caption my-[0.1rem] mb-1">
                          {row.games} peliä ·{" "}
                          <span
                            className={
                              row.winRate >= 50
                                ? "font-bold text-[#6ee7a0]"
                                : "font-bold text-[#ff8a8a]"
                            }
                          >
                            {formatNum(row.winRate, 0)}% WR
                          </span>
                        </p>
                        <p className="mb-[0.35rem] flex flex-wrap gap-x-3 gap-y-2 text-[0.68rem] font-semibold">
                          <StatValue
                            value={row.avgKills}
                            formatted={`K ${formatNum(row.avgKills, 1)}`}
                            tier={killsPerGameTier(row.avgKills)}
                          />
                          <StatValue
                            value={row.avgDeaths}
                            formatted={`D ${formatNum(row.avgDeaths, 1)}`}
                            tier={deathsPerGameTier(row.avgDeaths)}
                          />
                          <StatValue
                            value={row.avgAssists}
                            formatted={`A ${formatNum(row.avgAssists, 1)}`}
                            tier={assistsPerGameTier(row.avgAssists)}
                          />
                          <StatValue
                            value={row.kda}
                            formatted={`KDA ${formatNum(row.kda, 2)}`}
                            tier={kdaTier(row.kda)}
                          />
                          <StatValue
                            value={row.csPerMin}
                            formatted={`CS/m ${formatNum(row.csPerMin, 2)}`}
                            tier={csPerMinTier(row.csPerMin)}
                          />
                          <StatValue
                            value={row.dpm}
                            formatted={`DPM ${formatNum(row.dpm, 0)}`}
                            tier={dpmTier(row.dpm)}
                          />
                        </p>
                        <div className="h-[3px] overflow-hidden rounded-[2px] bg-[rgba(255,138,138,0.35)]">
                          <div
                            className="h-full min-w-[2px] rounded-[2px] bg-[#6ee7a0]"
                            style={{ width: `${String(row.winRate)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[0.7rem] text-[#9a9ab8]">
                          {row.wins}V {row.losses}H
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
                {showChampToggle ? (
                  <button
                    type="button"
                    className="mt-3 hidden w-full cursor-pointer rounded-lg border border-[var(--surface-card-border)] bg-[rgba(45,45,68,0.5)] px-3 py-[0.55rem] text-center font-jetbrains text-xs font-semibold text-[#5cc2ff] transition-[background,border-color] duration-150 hover:border-[rgba(123,147,255,0.35)] hover:bg-[rgba(123,147,255,0.12)] max-[960px]:block"
                    aria-expanded={champStatsExpanded}
                    onClick={() => {
                      setChampStatsExpanded((expanded) => !expanded);
                    }}
                  >
                    {champStatsExpanded
                      ? copy.SHOW_LESS_CHAMPS
                      : copy.SHOW_MORE_CHAMPS.replace("{count}", String(hiddenChampionCount))}
                  </button>
                ) : null}
              </section>
            ) : null}
          </aside>

          <div className="flex min-w-0 flex-col gap-6">
            {seasonStats.length > 0 ? (
              <section className="surface-card px-6 py-5">
                <h2 className={`${sectionHeadingClass} mb-[0.85rem]`}>FPL-kausitilastot</h2>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-[0.85rem]">
                  {seasonStats.map((seasonRow) => (
                    <article
                      key={`${seasonRow.season}-${seasonRow.teamId}`}
                      className="rounded-[10px] border border-[var(--surface-card-border)] bg-[rgba(45,45,68,0.35)] px-4 py-[0.85rem] text-left"
                    >
                      <div className="mb-[0.6rem] flex items-center justify-between gap-2">
                        <h3 className="fh-title-eyebrow text-[0.82rem]">{seasonRow.season}</h3>
                        <TeamLogo
                          teamId={seasonRow.teamId}
                          alt={seasonRow.teamId}
                          className="h-7 w-7 shrink-0 object-contain"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-[0.35rem] text-[0.78rem]">
                        <StatValue
                          value={seasonRow.kda}
                          formatted={`KDA ${formatNum(seasonRow.kda, 2)}`}
                          tier={kdaTier(seasonRow.kda)}
                        />
                        <StatValue
                          value={seasonRow.kills}
                          formatted={`K ${formatNum(seasonRow.kills, 1)}`}
                          tier={killsPerGameTier(seasonRow.kills)}
                        />
                        <StatValue
                          value={seasonRow.deaths}
                          formatted={`D ${formatNum(seasonRow.deaths, 1)}`}
                          tier={deathsPerGameTier(seasonRow.deaths)}
                        />
                        <StatValue
                          value={seasonRow.assists}
                          formatted={`A ${formatNum(seasonRow.assists, 1)}`}
                          tier={assistsPerGameTier(seasonRow.assists)}
                        />
                        <StatValue
                          value={seasonRow.csPerMin}
                          formatted={`CS/m ${formatNum(seasonRow.csPerMin, 2)}`}
                          tier={csPerMinTier(seasonRow.csPerMin)}
                        />
                        <StatValue
                          value={seasonRow.kpPct}
                          formatted={`KP ${formatNum(seasonRow.kpPct, 1)}%`}
                          tier={kpPctTier(seasonRow.kpPct)}
                        />
                        <StatValue
                          value={seasonRow.dpm}
                          formatted={`DPM ${formatNum(seasonRow.dpm, 0)}`}
                          tier={dpmTier(seasonRow.dpm)}
                        />
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="surface-card px-6 py-5">
              <div className="mb-[0.85rem] flex items-baseline justify-between gap-3">
                <h2 className={sectionHeadingClass}>Pelihistoria</h2>
                {totalGamesPlayed > 0 ? (
                  <span className="fh-caption whitespace-nowrap">
                    {totalGamesPlayed} peliä yhteensä
                  </span>
                ) : null}
              </div>
              {loading ? (
                <p className="px-0 py-8 text-center text-[#aaa]">Ladataan pelejä…</p>
              ) : null}
              {error ? <p className="px-0 py-8 text-center text-[#ff8a8a]">{error}</p> : null}
              {!loading && !error && sortedGames.length === 0 ? (
                <p className="px-0 py-8 text-center text-[#aaa]">Ei pelihistoriaa.</p>
              ) : null}
              {!loading && sortedGames.length > 0 ? (
                <div className="max-h-[70vh] overflow-auto rounded-[10px] border border-[var(--surface-card-border)] bg-[rgba(18,18,31,0.6)]">
                  <table className="w-full border-collapse text-[0.82rem] text-[#ddd]">
                    <thead>
                      <tr>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          Kausi
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          {copy.ROLE}
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          Champ
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          Joukkue
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          Vast.
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          Tulos
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          KDA
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          K
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          D
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          A
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          CS/m
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          DPM
                        </th>
                        <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                          KP%
                        </th>
                        {showMultikillColumn ? (
                          <th className="fh-table-head sticky top-0 z-[1] border-b border-[#2a2a40] bg-[var(--surface-card-bg)] px-[0.7rem] py-[0.55rem] text-left">
                            {copy.MULTIKILL}
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGames.map((gameRow) => {
                        const rowKda = computeKda(
                          gameRow.kills,
                          gameRow.deaths,
                          gameRow.assists,
                          gameRow.kda,
                        );
                        const multikillLabel = getMultikillLabel(gameRow);
                        const multikillTier = getMultikillTier(gameRow);
                        const gameRole = normalizeRole(gameRow.role);
                        const matchUrl =
                          gameRow.matchId != null && gameRow.matchId > 0
                            ? localized(matchDetailUrl(gameRow.matchId, gameRow.riotGameId))
                            : null;
                        const isWin = gameRow.result === "WIN";

                        return (
                          <tr
                            key={gameRow.id}
                            className={[
                              matchUrl
                                ? "cursor-pointer hover:bg-[rgba(28,152,217,0.08)] focus-visible:bg-[rgba(28,152,217,0.08)] focus-visible:outline-none"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={
                              matchUrl
                                ? () => {
                                    void navigate(matchUrl);
                                  }
                                : undefined
                            }
                            onKeyDown={
                              matchUrl
                                ? (event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      void navigate(matchUrl);
                                    }
                                  }
                                : undefined
                            }
                            tabIndex={matchUrl ? 0 : undefined}
                            role={matchUrl ? "link" : undefined}
                            aria-label={matchUrl ? copy.VIEW_MATCH : undefined}
                          >
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              {gameRow.season}
                            </td>
                            <td className="w-9 whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-center align-middle [&_.player-role-badge]:inline-flex [&_.player-role-badge]:justify-center">
                              {gameRole ? (
                                <PlayerRoleBadge role={gameRole} small />
                              ) : (
                                <span aria-hidden="true">—</span>
                              )}
                            </td>
                            <td className="min-w-0 whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              {gameRow.champion ? (
                                <span
                                  className="inline-flex min-w-0 items-center gap-[0.45rem]"
                                  title={gameRow.champion}
                                >
                                  <img
                                    src={championIconUrl(gameRow.champion)}
                                    alt={gameRow.champion}
                                    width={26}
                                    height={26}
                                    loading="lazy"
                                    className="h-[1.65rem] w-[1.65rem] shrink-0 rounded-full border border-[#3a3a5a] bg-[#12121f] object-cover"
                                  />
                                  <span className="max-w-[5.5rem] overflow-hidden text-ellipsis whitespace-nowrap text-[0.78rem] text-[#d8d8ef]">
                                    {gameRow.champion}
                                  </span>
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-center">
                              <TeamLogo
                                teamId={gameRow.teamId}
                                alt={gameRow.teamId}
                                className="inline h-[1.65rem] w-[1.65rem] align-middle object-contain"
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-center">
                              {gameRow.opponentTeam ? (
                                <TeamLogo
                                  teamId={gameRow.opponentTeam}
                                  alt={gameRow.opponentTeam}
                                  className="inline h-[1.65rem] w-[1.65rem] align-middle object-contain"
                                />
                              ) : (
                                "—"
                              )}
                            </td>
                            <td
                              className={`whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left font-bold ${isWin ? "text-[#6ee7a0]" : "text-[#ff8a8a]"}`}
                            >
                              {isWin ? "V" : "H"}
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={rowKda}
                                formatted={formatNum(rowKda, 2)}
                                tier={kdaTier(rowKda)}
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={gameRow.kills}
                                formatted={formatNum(gameRow.kills, 0)}
                                tier={killsPerGameTier(gameRow.kills)}
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={gameRow.deaths}
                                formatted={formatNum(gameRow.deaths, 0)}
                                tier={deathsPerGameTier(gameRow.deaths)}
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={gameRow.assists}
                                formatted={formatNum(gameRow.assists, 0)}
                                tier={assistsPerGameTier(gameRow.assists)}
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={gameRow.csPerMin}
                                formatted={formatNum(gameRow.csPerMin, 1)}
                                tier={csPerMinTier(gameRow.csPerMin)}
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={gameRow.dpm}
                                formatted={formatNum(gameRow.dpm, 0)}
                                tier={dpmTier(gameRow.dpm)}
                              />
                            </td>
                            <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-left">
                              <StatValue
                                value={gameRow.kpPct}
                                formatted={`${formatNum(gameRow.kpPct, 0)}%`}
                                tier={kpPctTier(gameRow.kpPct)}
                              />
                            </td>
                            {showMultikillColumn ? (
                              <td className="whitespace-nowrap border-b border-[#2a2a40] px-[0.7rem] py-[0.55rem] text-center">
                                {multikillLabel && multikillTier ? (
                                  <span
                                    className={`inline-block rounded px-[0.45rem] py-[0.12rem] text-[0.68rem] font-bold uppercase tracking-[0.3px] ${multikillTierClass[multikillTier] ?? ""}`}
                                  >
                                    {multikillLabel}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {!loading ? <StatTierLegend className="mt-4 border-0 px-0 pb-0" /> : null}
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
};
